/**
 * GMA-1629 (CompassCare Web 14/14 — Audit Log + Settings) — pure model.
 *
 * Two AC-central pure pieces the Audit + Settings screens consume:
 *  - Audit Log: an immutable, filterable event trail. `filterAuditEntries` is
 *    NON-mutating (never edits/deletes — the UI is append-only) and every
 *    filter is optional and fail-open.
 *  - Settings thresholds: `resolveThreshold` surfaces BOTH the agency default
 *    and the client value and flags whether the client value overrides the
 *    default (AC: "threshold override shows both agency default and client
 *    value").
 *
 * Everything fails safe: bad input yields a benign result, never a throw.
 */

// Auditable event categories (AC list). Kept as a frozen vocabulary so the
// Audit filter chips and the writers agree on one taxonomy.
export const AUDIT_EVENT_TYPES = Object.freeze([
  "alert_lifecycle",
  "contact_attempt",
  "sitrep",
  "access_instruction_view",
  "broadcast_send",
  "config_change",
  "report_export",
  "permission_change",
]);

const toTime = (v) => {
  if (v === null || v === undefined) return null;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
};

/**
 * Filter an audit trail. Returns a NEW array (input never mutated), original
 * order preserved. Every filter key is optional; an absent/blank filter adds no
 * constraint (fail-open). Multiple filters combine with AND.
 *
 * @param {Array} entries {id, actor, clientId, eventType, severity, timestamp}
 * @param {object} filters {actor, clientId, eventType, severity, from, to}
 * @returns {Array}
 */
export function filterAuditEntries(entries, filters) {
  const list = Array.isArray(entries) ? entries : [];
  const f = filters && typeof filters === "object" ? filters : {};
  const from = toTime(f.from);
  const to = toTime(f.to);

  return list.filter((e) => {
    if (!e || typeof e !== "object") return false;
    if (f.actor && e.actor !== f.actor) return false;
    if (f.clientId && e.clientId !== f.clientId) return false;
    if (f.eventType && e.eventType !== f.eventType) return false;
    if (f.severity && e.severity !== f.severity) return false;
    if (from !== null || to !== null) {
      const t = toTime(e.timestamp);
      if (t === null) return false;
      if (from !== null && t < from) return false;
      if (to !== null && t > to) return false;
    }
    return true;
  });
}

// Finite guard: rejects NaN AND Infinity/-Infinity so a non-finite value can't
// leak in as a "threshold".
const isNum = (v) => typeof v === "number" && Number.isFinite(v);

/**
 * Resolve an effective threshold from a per-client value and the agency default,
 * surfacing both plus whether the client value is an override.
 *
 * @param {number|null|undefined} clientValue
 * @param {number|null|undefined} agencyDefault
 * @returns {{value: (number|null), isOverride: boolean, agencyDefault: (number|null)}}
 */
export function resolveThreshold(clientValue, agencyDefault) {
  const def = isNum(agencyDefault) ? agencyDefault : null;
  if (!isNum(clientValue)) {
    return { value: def, isOverride: false, agencyDefault: def };
  }
  return {
    value: clientValue,
    isOverride: def === null ? true : clientValue !== def,
    agencyDefault: def,
  };
}
